/**
 * Shared URL parameter helpers for dynamic collection navigation,
 * storefront filtering, and pagination.
 */

const CollectionUrlParams = {
  TAB_COLLECTION_KEY: 'tab-collection',

  INTERNAL_PARAMS: new Set(['sections', 'section_id', '_']),

  FILTER_PARAMS: new Set(['page', 'sort_by', 'q', 'type']),

  isFilterParam(key) {
    return key.startsWith('filter.') || this.FILTER_PARAMS.has(key);
  },

  isPreservableParam(key) {
    return !this.INTERNAL_PARAMS.has(key);
  },

  getSearchParams(url = window.location.href) {
    return new URL(url, window.location.origin).searchParams;
  },

  getHubCollectionHandle() {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    return pathParts[0] === 'collections' ? pathParts[1] : null;
  },

  getActiveTabCollection() {
    if (window.__activeTabCollection) {
      return window.__activeTabCollection;
    }

    const fromUrl = this.getSearchParams().get(this.TAB_COLLECTION_KEY);
    if (fromUrl) return fromUrl;

    const pathHandle = this.getHubCollectionHandle();
    if (pathHandle && pathHandle !== 'frontpage') {
      return pathHandle;
    }

    return (
      document.querySelector('[data-collection-container]')?.dataset.activeCollection ||
      document.querySelector('[data-collection-banner]')?.dataset.collectionHandle ||
      null
    );
  },

  setActiveTabCollection(collectionHandle) {
    window.__activeTabCollection = collectionHandle || null;
  },

  clearFilterParams(searchParams) {
    [...searchParams.keys()].forEach((key) => {
      if (this.isFilterParam(key)) {
        searchParams.delete(key);
      }
    });
  },

  copyPreservableParams(source, target, { exclude = [] } = {}) {
    const excluded = new Set([...this.INTERNAL_PARAMS, ...exclude]);

    source.forEach((value, key) => {
      if (!excluded.has(key)) {
        target.set(key, value);
      }
    });
  },

  buildCollectionUrl(collectionHandle, { clearFilters = true, preserveParams = null } = {}) {
    const url = new URL(window.location.href);

    if (clearFilters) {
      this.clearFilterParams(url.searchParams);
    }

    if (preserveParams) {
      this.copyPreservableParams(preserveParams, url.searchParams, {
        exclude: [this.TAB_COLLECTION_KEY]
      });
    }

    if (collectionHandle) {
      url.searchParams.set(this.TAB_COLLECTION_KEY, collectionHandle);
    } else {
      url.searchParams.delete(this.TAB_COLLECTION_KEY);
    }

    url.hash = '';
    return url;
  },

  buildSectionFetchUrl(sectionIds, collectionHandle, { preserveFilters = false } = {}) {
    const hubHandle = this.getHubCollectionHandle();
    const hubPathname = window.location.pathname;
    const useHubPath =
      !collectionHandle || (hubHandle && collectionHandle === hubHandle);

    const pathname = useHubPath
      ? hubPathname
      : `/collections/${collectionHandle}`;

    const params = new URLSearchParams();

    if (preserveFilters) {
      this.copyPreservableParams(this.getSearchParams(), params, {
        exclude: [this.TAB_COLLECTION_KEY]
      });
    }

    if (useHubPath && collectionHandle) {
      params.set(this.TAB_COLLECTION_KEY, collectionHandle);
    } else {
      params.delete(this.TAB_COLLECTION_KEY);
    }

    params.set('sections', sectionIds.join(','));
    params.set('_', String(Date.now()));

    return `${window.location.origin}${pathname}?${params.toString()}`;
  },

  mergeIntoFetchUrl(fetchUrl, sourceSearch = window.location.search) {
    const url = new URL(fetchUrl, window.location.origin);
    const source = new URLSearchParams(sourceSearch);

    this.copyPreservableParams(source, url.searchParams, {
      exclude: ['page']
    });

    return url.toString();
  },

  buildLoadMoreFetchUrl(nextPageUrl, sectionId) {
    const url = new URL(nextPageUrl, window.location.origin);

    this.copyPreservableParams(this.getSearchParams(), url.searchParams, {
      exclude: ['page', this.TAB_COLLECTION_KEY]
    });

    if (sectionId) {
      url.searchParams.set('sections', sectionId);
    }

    url.searchParams.set('_', String(Date.now()));
    return url.toString();
  },

  updateBrowserUrl(collectionHandle, options = {}) {
    const url = this.buildCollectionUrl(collectionHandle, options);
    history.replaceState({ collectionHandle: collectionHandle || null }, '', url.toString());
    return url;
  }
};

window.CollectionUrlParams = CollectionUrlParams;
